import React, { Component, createRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { ProgressSpinner } from 'primereact/progressspinner';
import AdminServices from '../../services/axiosServices/apiServices/adminServices';

const formatDuration = (minutes) => {
    if (minutes === null || minutes === undefined) return '—';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
};

const FEEDBACK_LABELS = {
    welcoming_feel:     'Welcoming feel of home',
    cleanliness:        'Cleanliness & tidiness',
    safety_security:    'Safety & security',
    staff_friendly:     'Staff friendly & respectful',
    staff_knew_person:  'Staff knew young person',
    staff_communicated: 'Staff communicated clearly',
    yp_comfortable:     'YP comfortable & safe',
    yp_happy:           'YP happy or settled',
    home_calm:          'Home calm & supportive',
    overall_rating:     'Overall visit rating',
    yp_well_supported:  'YP well supported',
    open_feedback:      'Additional comments',
    completed_name:     'Completed by',
    profession:         'Profession / Organisation',
    home_visited:       'Home visited',
    visit_date:         'Date of visit',
    contact_followup:   'May we contact you?',
    contact_detail:     'Contact detail',
};

const RATING_MAP = { '5': 'Excellent', '4': 'Very good', '3': 'Good', '2': 'Fair', '1': 'Poor' };
const ratingFields = ['welcoming_feel', 'cleanliness', 'safety_security', 'staff_friendly', 'staff_knew_person', 'staff_communicated'];

const escapeCSV = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
const fmtDateTime = (v) => v ? new Date(v).toLocaleString('en-GB') : '';

export default class VisitorReportsList extends Component {
    searchTimeout = null;

    constructor(props) {
        super(props);
        this.adminServices = new AdminServices();
        this.dt = createRef();
        this.state = {
            reportData: [],
            houses: [],
            totalRecords: 0,
            isLoading: false,
            searchInput: '',
            dateRange: null,
            filterDetails: {
                houseId: '',
                startDate: '',
                endDate: '',
                start: 0,
                length: -1,
                search: '',
                sortField: 'vl.signintime',
                sortOrder: 'desc',
            },
            feedbackModal: null,
            exportModal: false,
            exportFormat: null,
            includeFeedback: false,
        };
    }

    componentDidMount() { this.getReports(); }
    componentWillUnmount() { clearTimeout(this.searchTimeout); }

    getReports = () => {
        const { filterDetails } = this.state;
        const request = [
            filterDetails.houseId, filterDetails.startDate, filterDetails.endDate,
            filterDetails.start, filterDetails.length, filterDetails.search,
            filterDetails.sortField, filterDetails.sortOrder,
        ];
        this.setState({ isLoading: true });
        this.adminServices.getVisitorReports(request).then((response) => {
            if (response?.success === 1) {
                this.setState({
                    reportData: response.data || [],
                    totalRecords: response.totalRecords || 0,
                    houses: response.houses || [],
                });
            }
            this.setState({ isLoading: false });
        }).catch(() => this.setState({ isLoading: false }));
    };

    handleSearch = (value) => {
        this.setState({ searchInput: value });
        clearTimeout(this.searchTimeout);
        this.searchTimeout = setTimeout(() => {
            this.setState(
                (prev) => ({ filterDetails: { ...prev.filterDetails, search: value } }),
                this.getReports
            );
        }, 500);
    };

    handleChangeDateRange = (value) => {
        let details = { ...this.state.filterDetails };
        details.startDate = value?.[0] ? new Date(value[0]).toISOString().slice(0, 10) : '';
        details.endDate   = value?.[1] ? new Date(value[1]).toISOString().slice(0, 10) : '';
        this.setState({ dateRange: value, filterDetails: details }, () => {
            if (details.startDate && details.endDate) this.getReports();
        });
    };

    handleHouseChange = (e) => {
        this.setState(
            (prev) => ({ filterDetails: { ...prev.filterDetails, houseId: e.target.value } }),
            this.getReports
        );
    };

    resetFilter = () => {
        clearTimeout(this.searchTimeout);
        this.setState({
            searchInput: '',
            dateRange: null,
            filterDetails: {
                houseId: '', startDate: '', endDate: '',
                start: 0, length: -1, search: '',
                sortField: 'vl.signintime', sortOrder: 'desc',
            },
        }, this.getReports);
    };

    // ── Export ──────────────────────────────────────────────
    buildExportData = (includeFeedback = false) => this.state.reportData.map((r, i) => {
        const base = {
            'Sr No':          i + 1,
            'House':          r.housename || '',
            'First Name':     r.firstname || '',
            'Surname':        r.surname || '',
            'Organisation':   r.organisation || '',
            'Person Visiting':r.personvisiting || '',
            'Sign In':        fmtDateTime(r.signintime),
            'Sign Out':       r.signouttime ? fmtDateTime(r.signouttime) : 'Still on site',
            'Duration (min)': r.durationminutes ?? '',
            'Auto Sign-Out':  r.autosignout ? 'Yes' : 'No',
            'Has Feedback':   r.formdata ? 'Yes' : 'No',
        };
        if (includeFeedback && r.formdata) {
            let fb = {};
            try { fb = JSON.parse(r.formdata); } catch {}
            Object.entries(FEEDBACK_LABELS).forEach(([key, label]) => {
                if (key !== 'home_visited' && key !== 'visit_date') {
                    base[`FB: ${label}`] = fb[key] ?? '';
                }
            });
        }
        return base;
    });

    exportCSV = () => {
        this.setState({ exportModal: true, exportFormat: 'csv', includeFeedback: false });
    };

    doExportCSV = (includeFeedback) => {
        const data = this.buildExportData(includeFeedback);
        const headers = Object.keys(data[0] || {});
        const csv = [headers, ...data.map(Object.values)]
            .map((r) => r.map(escapeCSV).join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `visitor-reports-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    saveAsExcelFile = (buffer, fileName) => {
        import('file-saver').then((module) => {
            if (module && module.default) {
                const EXCEL_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8';
                const data = new Blob([buffer], { type: EXCEL_TYPE });
                module.default.saveAs(data, fileName + '.xlsx');
            }
        });
    };

    exportExcel = () => {
        this.setState({ exportModal: true, exportFormat: 'excel', includeFeedback: false });
    };

    doExport = () => {
        const { includeFeedback, exportFormat } = this.state;
        this.setState({ exportModal: false });
        if (exportFormat === 'csv') { this.doExportCSV(includeFeedback); return; }
        import('xlsx').then((xlsx) => {
            const worksheet = xlsx.utils.json_to_sheet(this.buildExportData(includeFeedback));
            const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
            const excelBuffer = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });
            this.saveAsExcelFile(excelBuffer, `visitor-reports-${new Date().toISOString().slice(0, 10)}`);
        });
    };

    // ── Cell templates ───────────────────────────────────────
    signInTimeTemplate  = (r) => <span>{fmtDateTime(r.signintime) || '—'}</span>;

    signOutTimeTemplate = (r) => {
        if (!r.signouttime) return <span className="text-orange-500 text-sm font-medium">Still on site</span>;
        return (
            <span>
                {fmtDateTime(r.signouttime)}
                {r.autosignout === 1 && <span className="ml-1 text-xs text-gray-400">(auto)</span>}
            </span>
        );
    };



    durationTemplate   = (r) => <span>{formatDuration(r.durationminutes)}</span>;

    downloadTemplate = (r) => (
        <button
            onClick={() => this.downloadVisitorPDF(r)}
            className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors border-0 cursor-pointer"
            title="Download PDF"
        >
            <i className="pi pi-download mr-1" />
            PDF
        </button>
    );

    downloadVisitorPDF = (r) => {
        import('jspdf').then(({ default: jsPDF }) => {
            import('jspdf-autotable').then(({ default: autoTable }) => {
                const doc = new jsPDF();
                const blue = [0, 74, 173];

                doc.setFillColor(...blue);
                doc.rect(0, 0, 210, 22, 'F');
                doc.setTextColor(255, 255, 255);
                doc.setFontSize(14);
                doc.setFont('helvetica', 'bold');
                doc.text('Visitor Record', 14, 14);

                doc.setTextColor(34, 54, 69);
                doc.setFontSize(10);
                doc.setFont('helvetica', 'normal');

                const rows = [
                    ['House', r.housename || '—'],
                    ['First Name', r.firstname || '—'],
                    ['Surname', r.surname || '—'],
                    ['Organisation', r.organisation || '—'],
                    ['Person Visiting', r.personvisiting || '—'],
                    ['Sign In', fmtDateTime(r.signintime) || '—'],
                    ['Sign Out', r.signouttime ? fmtDateTime(r.signouttime) + (r.autosignout ? ' (auto)' : '') : 'Still on site'],
                    ['Duration', formatDuration(r.durationminutes)],
                ];

                autoTable(doc, {
                    startY: 28,
                    head: [['Field', 'Value']],
                    body: rows,
                    headStyles: { fillColor: blue, textColor: 255, fontStyle: 'bold' },
                    alternateRowStyles: { fillColor: [249, 250, 251] },
                    styles: { fontSize: 10 },
                    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 55 } },
                });

                let nextY = doc.lastAutoTable.finalY;

                if (r.signature) {
                    const sigY = nextY + 8;
                    doc.setFontSize(9);
                    doc.setFont('helvetica', 'bold');
                    doc.setTextColor(34, 54, 69);
                    doc.text('Visitor Signature', 14, sigY + 4);
                    doc.setDrawColor(209, 213, 219);
                    doc.setLineWidth(0.4);
                    doc.rect(14, sigY + 8, 100, 30);
                    try {
                        doc.addImage(r.signature, 'PNG', 15, sigY + 9, 98, 28);
                    } catch {}
                    nextY = sigY + 38;
                }

                if (r.formdata) {
                    let fb = {};
                    try { fb = JSON.parse(r.formdata); } catch {}

                    const fbRows = Object.entries(FEEDBACK_LABELS)
                        .filter(([k]) => k !== 'home_visited' && k !== 'visit_date')
                        .map(([k, label]) => {
                            const val = fb[k];
                            if (val === null || val === undefined || val === '') return null;
                            const display = ratingFields.includes(k) && RATING_MAP[String(val)]
                                ? `${val} – ${RATING_MAP[String(val)]}`
                                : String(val);
                            return [label, display];
                        })
                        .filter(Boolean);

                    if (fbRows.length > 0) {
                        autoTable(doc, {
                            startY: nextY + 8,
                            head: [['Feedback Field', 'Response']],
                            body: fbRows,
                            headStyles: { fillColor: [0, 119, 182], textColor: 255, fontStyle: 'bold' },
                            alternateRowStyles: { fillColor: [239, 246, 255] },
                            styles: { fontSize: 9 },
                            columnStyles: { 0: { fontStyle: 'bold', cellWidth: 75 } },
                        });
                    }
                }

                const name = `${r.firstname || 'visitor'}-${r.surname || ''}-${new Date(r.signintime).toISOString().slice(0, 10)}`;
                doc.save(`${name}.pdf`);
            });
        });
    };

    feedbackTemplate   = (r) => {
        if (!r.formdata) return <span className="text-xs text-gray-400">No feedback</span>;
        return (
            <button
                onClick={() => {
                    let parsed = {};
                    try { parsed = JSON.parse(r.formdata); } catch {}
                    this.setState({ feedbackModal: { visitor: `${r.firstname} ${r.surname}`, data: parsed } });
                }}
                className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 hover:bg-blue-200 transition-colors border-0 cursor-pointer"
            >
                View Feedback
            </button>
        );
    };

    // ── Feedback modal ───────────────────────────────────────
    renderFeedbackModal = () => {
        const { feedbackModal } = this.state;
        if (!feedbackModal) return null;
        const { visitor, data } = feedbackModal;

        const sections = [
            { title: 'Arrival & First Impressions', fields: ['welcoming_feel', 'cleanliness', 'safety_security'] },
            { title: 'Staff & Care',                fields: ['staff_friendly', 'staff_knew_person', 'staff_communicated'] },
            { title: "Young Person's Wellbeing",    fields: ['yp_comfortable', 'yp_happy', 'home_calm'] },
            { title: 'Overall Feedback',            fields: ['overall_rating', 'yp_well_supported'] },
            { title: 'Additional Comments',         fields: ['open_feedback'] },
            { title: 'Completed By',                fields: ['completed_name', 'profession', 'home_visited', 'visit_date', 'contact_followup', 'contact_detail'] },
        ];

        const displayValue = (key, val) => {
            if (!val && val !== 0) return '—';
            if (ratingFields.includes(key)) return `${val} – ${RATING_MAP[String(val)] || val}`;
            return String(val);
        };

        return (
            <div
                className="fixed inset-0 z-50 flex items-center justify-center"
                style={{ background: 'rgba(0,0,0,0.5)' }}
                onClick={(e) => { if (e.target === e.currentTarget) this.setState({ feedbackModal: null }); }}
            >
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 flex flex-col" style={{ maxHeight: '88vh' }}>
                    <div className="flex items-center justify-between p-5 border-b border-gray-200" style={{ background: '#004AAD', borderRadius: '16px 16px 0 0' }}>
                        <div>
                            <h3 className="text-white font-bold text-lg m-0">Visitor Feedback</h3>
                            <p className="text-blue-200 text-sm m-0">{visitor}</p>
                        </div>
                        <button onClick={() => this.setState({ feedbackModal: null })} className="text-white text-xl font-bold border-0 bg-transparent cursor-pointer w-8 h-8 flex items-center justify-center hover:text-blue-200">×</button>
                    </div>

                    <div className="overflow-y-auto p-5 flex flex-col gap-4">
                        {sections.map(({ title, fields }) => {
                            const hasAny = fields.some((f) => data[f] !== null && data[f] !== undefined && data[f] !== '');
                            if (!hasAny) return null;
                            return (
                                <div key={title}>
                                    <p className="text-xs font-bold text-blue-800 uppercase tracking-wide mb-2">{title}</p>
                                    <div className="bg-gray-50 rounded-xl overflow-hidden border border-gray-100">
                                        {fields.map((f) => {
                                            const val = data[f];
                                            if (val === null || val === undefined || val === '') return null;
                                            if (f === 'open_feedback') {
                                                return (
                                                    <div key={f} className="px-4 py-3">
                                                        <p className="text-gray-500 text-xs font-medium mb-1">{FEEDBACK_LABELS[f]}</p>
                                                        <p className="text-gray-800 text-sm" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{val}</p>
                                                    </div>
                                                );
                                            }
                                            return (
                                                <div key={f} className="flex items-center gap-3 px-4 py-2 border-b border-gray-100 last:border-0">
                                                    <span className="text-gray-500 text-sm" style={{ minWidth: 200, flexShrink: 0 }}>{FEEDBACK_LABELS[f] || f}</span>
                                                    <span className="text-gray-800 text-sm font-semibold">{displayValue(f, val)}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    <div className="p-4 border-t border-gray-100">
                        <button onClick={() => this.setState({ feedbackModal: null })} className="w-full py-2 rounded-lg border border-gray-300 text-gray-600 text-sm font-semibold hover:bg-gray-50 transition-colors bg-white cursor-pointer">
                            Close
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    renderExportModal = () => {
        const { exportModal, includeFeedback, exportFormat } = this.state;
        if (!exportModal) return null;
        const label = exportFormat === 'csv' ? 'CSV' : 'Excel';
        return (
            <div
                className="fixed inset-0 z-50 flex items-center justify-center"
                style={{ background: 'rgba(0,0,0,0.5)' }}
                onClick={(e) => { if (e.target === e.currentTarget) this.setState({ exportModal: false }); }}
            >
                <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4">
                    <div className="p-5 border-b border-gray-200" style={{ background: '#004AAD', borderRadius: '16px 16px 0 0' }}>
                        <h3 className="text-white font-bold text-lg m-0">Export {label}</h3>
                        <p className="text-blue-200 text-sm m-0 mt-1">Choose export options</p>
                    </div>
                    <div className="p-5">
                        <label className="flex items-center gap-3 cursor-pointer select-none">
                            <input
                                type="checkbox"
                                checked={includeFeedback}
                                onChange={(e) => this.setState({ includeFeedback: e.target.checked })}
                                className="w-4 h-4 accent-blue-700"
                            />
                            <span className="text-sm font-medium text-gray-700">Include visitor feedback data</span>
                        </label>
                        <p className="text-xs text-gray-400 mt-1 ml-7">Adds feedback columns for records that have feedback.</p>
                    </div>
                    <div className="flex gap-3 px-5 pb-5">
                        <button
                            onClick={this.doExport}
                            className="flex-1 py-2 rounded-lg text-white text-sm font-semibold border-0 cursor-pointer"
                            style={{ background: '#1d6f42' }}
                        >
                            Download
                        </button>
                        <button
                            onClick={() => this.setState({ exportModal: false })}
                            className="flex-1 py-2 rounded-lg border border-gray-300 text-gray-600 text-sm font-semibold bg-white cursor-pointer hover:bg-gray-50"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        );
    };

    render() {
        const { reportData, houses, isLoading, searchInput, filterDetails, dateRange } = this.state;

        const paginatorRight = (
            <div className="flex gap-2">
                <Button label="Export CSV" icon="pi pi-file" className="export-btn" onClick={this.exportCSV} />
                <Button label="Export Excel" icon="pi pi-file-excel" className="export-btn" onClick={this.exportExcel} />
            </div>
        );

        return (
            <div className="visitor-reports" style={{ height: 'calc((100dvh / 0.85) - 45.8px)' }}>
                <div className="visitor-reports-inner h-full p-[30px] overflow-y-auto">
                    <div className="title flex items-center justify-between mb-[10px]">
                        <h1 className="text-[22px] m-0 font-semibold text-[#004AAD]" style={{ lineHeight: '30px' }}>Visitor Reports</h1>
                    </div>

                    <div className="user-table p-5 bg-white rounded-[16px]" style={{ border: '1px solid #DFDFDF' }}>
                        <div className="table-inner grid grid-cols-4 gap-5 items-end">
                            <div className="input-grp !mb-0">
                                <label>Search</label>
                                <InputText
                                    type="search"
                                    value={searchInput}
                                    onChange={(e) => this.handleSearch(e.target.value)}
                                    placeholder="Name, organisation, house..."
                                />
                            </div>

                            <div className="input-grp !mb-0">
                                <label>Filter by Date Range</label>
                                <Calendar
                                    value={dateRange}
                                    onChange={(e) => this.handleChangeDateRange(e.value)}
                                    selectionMode="range"
                                    placeholder="Select date range"
                                    dateFormat="yy-mm-dd"
                                    readOnlyInput
                                    numberOfMonths={2}
                                    maxDate={new Date()}
                                    style={{ height: '42px' }}
                                />
                            </div>

                            <div className="input-grp !mb-0">
                                <label>House</label>
                                <select value={filterDetails.houseId} onChange={this.handleHouseChange}>
                                    <option value="">All Houses</option>
                                    {houses.map((h) => (
                                        <option key={h.houseid} value={h.houseid}>{h.housename}</option>
                                    ))}
                                </select>
                            </div>

                            <Button
                                label="Reset filters"
                                className="w-fit h-fit"
                                onClick={this.resetFilter}
                                style={{ fontWeight: '400', lineHeight: '1' }}
                            />
                        </div>

                        <div className="sub-table rounded-xl shadow-sm overflow-hidden border border-gray-200 bg-white mt-[30px]">
                            <DataTable
                                ref={this.dt}
                                value={reportData}
                                paginator
                                rows={10}
                                rowsPerPageOptions={[10, 25, 50, 100]}
                                paginatorRight={paginatorRight}
                                responsiveLayout="scroll"
                                scrollable
                                scrollHeight="calc((100dvh / 0.85) - 420px)"
                                className="min-w-full"
                                emptyMessage="No visitor records found"
                                loading={isLoading}
                                loadingIcon={
                                    <div className="flex justify-center items-center w-full py-4">
                                        <ProgressSpinner style={{ width: '40px', height: '40px' }} strokeWidth="4" />
                                    </div>
                                }
                            >
                                <Column header="Sr No" body={(r, o) => o.rowIndex + 1} headerClassName="bg-blue-50 text-blue-800 font-medium" style={{ width: '4rem' }} />
                                <Column field="housename"     header="House"          sortable headerClassName="bg-blue-50 text-blue-800 font-medium" />
                                <Column field="firstname"     header="First Name"     sortable headerClassName="bg-blue-50 text-blue-800 font-medium" />
                                <Column field="surname"       header="Surname"        sortable headerClassName="bg-blue-50 text-blue-800 font-medium" />
                                <Column field="organisation"  header="Organisation"           headerClassName="bg-blue-50 text-blue-800 font-medium" />
                                <Column field="personvisiting" header="Person Visiting"        headerClassName="bg-blue-50 text-blue-800 font-medium" />
                                <Column header="Sign In"      body={this.signInTimeTemplate}  sortable sortField="vl.signintime" headerClassName="bg-blue-50 text-blue-800 font-medium" />
                                <Column header="Sign Out"     body={this.signOutTimeTemplate}  headerClassName="bg-blue-50 text-blue-800 font-medium" />
                                <Column header="Duration"     body={this.durationTemplate}    headerClassName="bg-blue-50 text-blue-800 font-medium" style={{ width: '7rem' }} />
                                <Column header="Feedback"     body={this.feedbackTemplate}    headerClassName="bg-blue-50 text-blue-800 font-medium" style={{ width: '9rem' }} />
                                <Column header="Download"     body={this.downloadTemplate}    headerClassName="bg-blue-50 text-blue-800 font-medium" style={{ width: '7rem' }} />
                            </DataTable>
                        </div>
                    </div>
                </div>

                {this.renderFeedbackModal()}
                {this.renderExportModal()}
            </div>
        );
    }
}
